import { useIntl } from 'react-intl'

import { type AppViewProps } from '@/components/templates/AppView'

export const useAppLabels = (): Pick<AppViewProps, 'labels'> => {
  const intl = useIntl()

  return {
    labels: {
      header: {
        information: {
          legend: {
            team: {
              title: intl.formatMessage({ id: 'legend.team.title' }),
              available: intl.formatMessage({ id: 'teamStatus.available' }),
              assigned: intl.formatMessage({ id: 'teamStatus.assigned' }),
              allocated: intl.formatMessage({ id: 'teamStatus.allocated' }),
              onboarded: intl.formatMessage({ id: 'teamStatus.onboarded' }),
              unfindable: intl.formatMessage({ id: 'teamStatus.unfindable' }),
              paused: intl.formatMessage({ id: 'teamStatus.paused' }),
              returning: intl.formatMessage({ id: 'teamStatus.returning' }),
              onboardFlagged: intl.formatMessage({ id: 'teamStatus.onboardFlagged' }),
            },
            request: {
              title: intl.formatMessage({ id: 'legend.request.title' }),
              pending: intl.formatMessage({ id: 'legend.request.pending' }),
              assigned: intl.formatMessage({ id: 'legend.request.assigned' }),
              allocated: intl.formatMessage({ id: 'legend.request.allocated' }),
              onboarded: intl.formatMessage({ id: 'legend.request.onboarded' }),
              unfindable: intl.formatMessage({ id: 'legend.request.unfindable' }),
              completed: intl.formatMessage({ id: 'legend.request.completed' }),
              cancelled: intl.formatMessage({ id: 'legend.request.cancelled' }),
              mobile: intl.formatMessage({ id: 'legend.request.mobile' }),
            },
            donation: {
              title: intl.formatMessage({ id: 'legend.donation.title' }),
              no: intl.formatMessage({ id: 'legend.donation.no' }),
              cash: intl.formatMessage({ id: 'legend.donation.cash' }),
              digital: intl.formatMessage({ id: 'legend.donation.digital' }),
              textSent: intl.formatMessage({ id: 'legend.donation.textSent' }),
              textClicked: intl.formatMessage({ id: 'legend.donation.textClicked' }),
            },
            teamMobileStatus: {
              title: intl.formatMessage({ id: 'legend.teamMobileStatus.title' }),
              noMobile: intl.formatMessage({ id: 'teamMobileStatus.noMobile' }),
              mobile: intl.formatMessage({ id: 'teamMobileStatus.mobile' }),
              notConnected: intl.formatMessage({ id: 'teamMobileStatus.notConnected' }),
            },
            carVariations: {
              title: intl.formatMessage({ id: 'legend.carVariations.title' }),
              vip: intl.formatMessage({ id: 'legend.carVariations.vip' }),
              electric: intl.formatMessage({ id: 'legend.carVariations.electric' }),
              manual: intl.formatMessage({ id: 'legend.carVariations.manual' }),
            },
            automation: {
              title: intl.formatMessage({ id: 'legend.automation.title' }),
              active: intl.formatMessage({ id: 'legend.automation.active' }),
              pending: intl.formatMessage({ id: 'legend.automation.pending' }),
              inactive: intl.formatMessage({ id: 'legend.automation.inactive' }),
            },
            requestCardState: {
              pending: intl.formatMessage({ id: 'legend.requestCardState.pending' }),
              automated: intl.formatMessage({ id: 'legend.requestCardState.automated' }),
              assigned: intl.formatMessage({ id: 'legend.requestCardState.assigned' }),
              allocated: intl.formatMessage({ id: 'legend.requestCardState.allocated' }),
              onboarded: intl.formatMessage({ id: 'legend.requestCardState.onboarded' }),
            },
          },
        },
        waitTime: {
          increase: intl.formatMessage({ id: 'header.waitTime.increase' }),
          automate: intl.formatMessage({ id: 'header.waitTime.automate' }),
        },
        mobileRequest: {
          mobileRequestOpen: intl.formatMessage({ id: 'header.mobileRequest.mobileRequestOpen' }),
          mobileRequestPaused: intl.formatMessage({ id: 'header.mobileRequest.mobileRequestPaused' }),
          mobileRequestClosed: intl.formatMessage({ id: 'header.mobileRequest.mobileRequestClosed' }),
        },
        mobileRequestStatusPopup: {
          openMessageTitle: intl.formatMessage({ id: 'header.mobileRequestStatusPopup.openMessageTitle' }),
          openMessageText: intl
            .formatMessage({ id: 'header.mobileRequestStatusPopup.openMessageText' })
            .replaceAll(String.raw`\n`, '\n'),
          exceedsThresholdMessageTitle: intl.formatMessage({
            id: 'header.mobileRequestStatusPopup.exceedsThresholdMessageTitle',
          }),
          exceedsThresholdMessageText: intl
            .formatMessage({
              id: 'header.mobileRequestStatusPopup.exceedsThresholdMessageText',
            })
            .replaceAll(String.raw`\n`, '\n'),
          pauseMessageTitle: intl.formatMessage({ id: 'header.mobileRequestStatusPopup.pauseMessageTitle' }),
          pauseMessageText: intl
            .formatMessage({ id: 'header.mobileRequestStatusPopup.pauseMessageText' })
            .replaceAll(String.raw`\n`, '\n'),
          adminPauseMessageTitle: intl.formatMessage({ id: 'header.mobileRequestStatusPopup.adminPauseMessageTitle' }),
          adminPauseMessageText: intl
            .formatMessage({ id: 'header.mobileRequestStatusPopup.adminPauseMessageText' })
            .replaceAll(String.raw`\n`, '\n'),
        },
        assignation: {
          manualSpecifications: {
            title: intl.formatMessage({ id: 'header.assignation.manual.title' }),
            description: intl.formatMessage({ id: 'header.assignation.manual.description' }),
            levelName: intl.formatMessage({ id: 'header.assignation.manual.levelName' }),
            items: [
              intl.formatMessage({ id: 'header.assignation.feature.assign' }),
              intl.formatMessage({ id: 'header.assignation.feature.allocate' }),
              intl.formatMessage({ id: 'header.assignation.feature.optimize' }),
            ],
          },
          solverSpecifications: {
            title: intl.formatMessage({ id: 'header.assignation.solver.title' }),
            description: intl.formatMessage({ id: 'header.assignation.solver.description' }),
            levelName: intl.formatMessage({ id: 'header.assignation.solver.levelName' }),
            items: [
              intl.formatMessage({ id: 'header.assignation.feature.assign' }),
              intl.formatMessage({ id: 'header.assignation.feature.allocate' }),
              intl.formatMessage({ id: 'header.assignation.feature.optimize' }),
            ],
          },
        },
      },
      navigation: {
        heading: intl.formatMessage({ id: 'navigation.heading' }),
        button: {
          map: intl.formatMessage({ id: 'navigation.map' }),
          list: intl.formatMessage({ id: 'navigation.list' }),
        },
      },
      sidepanel: { team: intl.formatMessage({ id: 'sidepanel.team' }) },
      automationPause: '',
      requestList: {
        pending: {
          distanceTime: intl.formatMessage({ id: 'requestList.distanceTime' }),
          assignManually: intl.formatMessage({ id: 'requestList.assignManually' }),
          close: intl.formatMessage({ id: 'requestList.close' }),
          selectAvailableTeam: intl.formatMessage({ id: 'requestList.selectAvailableTeam' }),
          cancelConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.cancelRequest' }),
            title: intl.formatMessage({ id: 'dialog.cancelRequest.title' }),
            description: intl.formatMessage({ id: 'dialog.cancelRequest.description' }),
            confirm: intl.formatMessage({ id: 'yes' }),
            cancel: intl.formatMessage({ id: 'no' }),
          },
          assignConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.assign' }),
            title: intl.formatMessage({ id: 'dialog.assignRequest.title' }),
            description: intl.formatMessage({ id: 'dialog.assignRequest.description' }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
        },
        assigned: {
          distanceTime: intl.formatMessage({ id: 'requestList.distanceTime' }),
          waitingForTeam: intl.formatMessage({ id: 'requestList.waitingForTeam' }),
          allocateManuallyConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.allocateManually' }),
            title: intl.formatMessage({ id: 'dialog.allocateRequest.title' }),
            description: intl.formatMessage({ id: 'dialog.allocateRequest.description' }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
          cancelConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.cancelRequest' }),
            title: intl.formatMessage({ id: 'dialog.cancelRequest.title' }),
            description: intl.formatMessage({ id: 'dialog.cancelRequest.description' }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
          putOnHoldConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.putOnHold' }),
            title: intl.formatMessage({ id: 'dialog.putOnHold.title' }),
            description: (teamNumber) => intl.formatMessage({ id: 'dialog.putOnHold.description' }, { teamNumber }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
        },
        allocated: {
          distanceTime: intl.formatMessage({ id: 'requestList.distanceTime' }),
          cancelConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.cancelRequest' }),
            title: intl.formatMessage({ id: 'dialog.cancelRequest.title' }),
            description: intl.formatMessage({ id: 'dialog.cancelRequest.description' }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
          customerNotFoundConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.customerNotFound' }),
            title: intl.formatMessage({ id: 'dialog.customerNotFound.title' }),
            description: (teamNumber) =>
              intl.formatMessage({ id: 'dialog.customerNotFound.description' }, { teamNumber }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
          customerTakenCareOfConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.customerTakenCareOf' }),
            title: intl.formatMessage({ id: 'dialog.clientOnboard.title' }),
            description: intl.formatMessage({ id: 'dialog.clientOnboard.description' }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
          putOnHoldConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.putOnHold' }),
            title: intl.formatMessage({ id: 'dialog.putOnHold.title' }),
            description: (teamNumber) => intl.formatMessage({ id: 'dialog.putOnHold.description' }, { teamNumber }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
        },
        onboarded: {
          distanceTime: intl.formatMessage({ id: 'requestList.distanceTime' }),
          arrivedAtDestinationConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.arrivedAtDestination' }),
            title: intl.formatMessage({ id: 'dialog.arrivedAtDestination.title' }),
            description: (teamNumber) =>
              intl.formatMessage({ id: 'dialog.arrivedAtDestination.description' }, { teamNumber }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
            receipt: intl.formatMessage({ id: 'dialog.arrivedAtDestination.receipt' }),
            donationPlaceholder: intl.formatMessage({ id: 'dialog.arrivedAtDestination.donationPlaceholder' }),
          },
          cancelConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.cancelRequest' }),
            title: intl.formatMessage({ id: 'dialog.cancelRequest.title' }),
            description: intl.formatMessage({ id: 'dialog.cancelRequest.description' }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
        },
        completed: {
          distanceTime: intl.formatMessage({ id: 'requestList.distanceTime' }),
          changeDonationConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.changeDonation' }),
            title: intl.formatMessage({ id: 'dialog.changeDonation.title' }),
            description: intl.formatMessage({ id: 'dialog.changeDonation.description' }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
            receipt: intl.formatMessage({ id: 'dialog.changeDonation.receipt' }),
          },
          reactivateConfirmDialog: {
            trigger: intl.formatMessage({ id: 'requestList.reactivate' }),
            title: intl.formatMessage({ id: 'dialog.reactivateRequest.title' }),
            description: intl.formatMessage({ id: 'dialog.reactivateRequest.description' }),
            confirm: intl.formatMessage({ id: 'confirm' }),
            cancel: intl.formatMessage({ id: 'cancel' }),
          },
        },
      },
    },
  }
}
